<div class="page-container">
  <div class="flex flex-col gap-8">
    <app-page-header
      title="Team members"
      description="Invite teammates to your workspace and manage pending invitations.">
      <lib-signal-button
        iconSrc="assets/icons/account-multiple-plus-outline.svg"
        (clicked)="openInviteMember()">
        Invite member
      </lib-signal-button>
    </app-page-header>

    @if (createdInvitations().length > 0 || skippedInvitations().length > 0) {
      <section class="rounded-2xl border border-neutral-200 bg-white p-6">
        <h2 class="text-lg font-medium text-neutral-950">Last invite result</h2>
        <p class="mt-1 text-sm text-neutral-500">
          Copy the invite link to share it. Pending invites also appear below.
        </p>

        <div class="mt-4 overflow-hidden rounded-xl border border-neutral-200">
          <table class="min-w-full divide-y divide-neutral-200 text-sm">
            <thead class="bg-neutral-50 text-left text-neutral-600">
              <tr>
                <th class="px-4 py-3 font-medium">Email</th>
                <th class="px-4 py-3 font-medium">Role</th>
                <th class="px-4 py-3 font-medium">Invite link</th>
                <th class="px-4 py-3 font-medium">Expires</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-neutral-100 bg-white">
              @for (invite of createdInvitations(); track invite.id) {
                <tr>
                  <td class="px-4 py-3 text-neutral-900">{{ invite.email }}</td>
                  <td class="px-4 py-3 text-neutral-700">{{ formatRole(invite.role) }}</td>
                  <td class="px-4 py-3">
                    <div class="flex items-center gap-2">
                      <span class="max-w-xs truncate text-neutral-600">{{ invite.inviteLink }}</span>
                      <button
                        type="button"
                        class="rounded-lg border border-neutral-200 px-2 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-50"
                        (click)="copyInviteLink(invite.inviteLink)">
                        Copy
                      </button>
                    </div>
                  </td>
                  <td class="px-4 py-3 text-neutral-600">{{ invite.expiresAt | date: 'mediumDate' }}</td>
                </tr>
              }
              @for (item of skippedInvitations(); track item.email) {
                <tr class="bg-amber-50/60">
                  <td class="px-4 py-3 text-neutral-900">{{ item.email }}</td>
                  <td class="px-4 py-3 text-neutral-500" colspan="3">{{ item.reason }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </section>
    }

    <section class="rounded-2xl border border-neutral-200 bg-white p-6">
      <h2 class="text-lg font-medium text-neutral-950">Pending invitations</h2>
      <p class="mt-1 text-sm text-neutral-500">
        Active invites waiting to be accepted. Links are only shown when invitations are created.
      </p>

      @if (pendingState.loading()) {
        <p class="mt-4 text-sm text-neutral-500">Loading invitations…</p>
      } @else if (pendingInvitations().length === 0) {
        <p class="mt-4 text-sm text-neutral-500">No pending invitations.</p>
      } @else {
        <div class="mt-4 overflow-hidden rounded-xl border border-neutral-200">
          <table class="min-w-full divide-y divide-neutral-200 text-sm">
            <thead class="bg-neutral-50 text-left text-neutral-600">
              <tr>
                <th class="px-4 py-3 font-medium">Email</th>
                <th class="px-4 py-3 font-medium">Role</th>
                <th class="px-4 py-3 font-medium">Sent</th>
                <th class="px-4 py-3 font-medium">Expires</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-neutral-100 bg-white">
              @for (invite of pendingInvitations(); track invite.id) {
                <tr>
                  <td class="px-4 py-3 text-neutral-900">{{ invite.email }}</td>
                  <td class="px-4 py-3 text-neutral-700">{{ formatRole(invite.role) }}</td>
                  <td class="px-4 py-3 text-neutral-600">{{ invite.createdAt | date: 'mediumDate' }}</td>
                  <td class="px-4 py-3 text-neutral-600">{{ invite.expiresAt | date: 'mediumDate' }}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </section>

    <section class="rounded-2xl border border-neutral-200 bg-white p-6">
      <h2 class="text-lg font-medium text-neutral-950">Members</h2>
      <p class="mt-1 text-sm text-neutral-500">People who currently have access to this workspace.</p>

      @if (membersState.loading()) {
        <p class="mt-4 text-sm text-neutral-500">Loading members…</p>
      } @else if (members().length === 0) {
        <p class="mt-4 text-sm text-neutral-500">No members found.</p>
      } @else {
        <div class="mt-4 overflow-hidden rounded-xl border border-neutral-200">
          <table class="min-w-full divide-y divide-neutral-200 text-sm">
            <thead class="bg-neutral-50 text-left text-neutral-600">
              <tr>
                <th class="px-4 py-3 font-medium">Name</th>
                <th class="px-4 py-3 font-medium">Email</th>
                <th class="px-4 py-3 font-medium">Role</th>
                <th class="px-4 py-3 font-medium">Joined</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-neutral-100 bg-white">
              @for (member of members(); track member.id) {
                <tr>
                  <td class="px-4 py-3 text-neutral-900">{{ member.name || '—' }}</td>
                  <td class="px-4 py-3 text-neutral-700">{{ member.email }}</td>
                  <td class="px-4 py-3 text-neutral-700">{{ formatRole(member.role) }}</td>
                  <td class="px-4 py-3 text-neutral-600">
                    @if (member.joinedAt) {
                      {{ member.joinedAt | date: 'mediumDate' }}
                    } @else {
                      —
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </section>
  </div>
</div>
